import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import authService from '../services/authService';
import { verifyCaptcha } from '../../../utils/captcha';
import CaptchaField from '../../../components/auth/CaptchaField';
import './LoginPage.css';
import AppLogo from '../../../components/common/AppLogo';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function ForgotPasswordPage() {
  const location = useLocation();
  const prefilledEmail =
    typeof location.state?.email === 'string' ? location.state.email.trim() : '';
  const [email, setEmail] = useState(prefilledEmail);
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [captchaExpected, setCaptchaExpected] = useState('');
  const [captchaKey, setCaptchaKey] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const refreshCaptcha = () => setCaptchaKey((k) => k + 1);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      return setError('Please enter your email address.');
    }
    if (!EMAIL_PATTERN.test(trimmedEmail)) {
      return setError('Please enter a valid email address.');
    }
    if (!captchaAnswer.trim()) {
      return setError('Please enter the verification code.');
    }
    if (!verifyCaptcha(captchaAnswer, captchaExpected)) {
      refreshCaptcha();
      setCaptchaAnswer('');
      return setError('Verification code is incorrect. Please try again.');
    }

    setLoading(true);
    try {
      const data = await authService.requestForgotPassword(trimmedEmail);
      setSuccess(
        data?.message ||
          'If an account exists for this email, a password reset link has been sent.'
      );
      setEmail('');
      setCaptchaAnswer('');
      refreshCaptcha();
    } catch (err) {
      setError(err.message || 'Could not send reset link. Please try again later.');
      refreshCaptcha();
      setCaptchaAnswer('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div
        className="login-left"
        style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/assets/loginimage.jpeg)` }}
      >
        <div className="login-overlay" />
        <div className="login-logo">
          <AppLogo variant="default" className="login-logo-img" />
          <div className="login-logo-name">
            Indusinformatics Structural Health Monitoring©
          </div>
        </div>
        <div className="login-features">
          <div className="login-feature">
            <div className="login-feature-icon">
              <i className="ti ti-mail-forward" />
            </div>
            <div>
              <h4>Reset your password</h4>
              <p>Enter your email and we will send you a secure link to choose a new password.</p>
            </div>
          </div>
          <div className="login-left-footer">
            <p className="login-left-security">
              <i className="ti ti-shield-check" />
              Your password is encrypted and stored securely
            </p>
            <p className="login-left-copyright">
              &copy; 2026 Indus Informatics. All rights reserved.
            </p>
          </div>
        </div>
      </div>

      <div className="login-right">
        <div className="login-card">
          <div className="login-icon-wrap">
            <i className="ti ti-key" style={{ fontSize: 28, color: '#1a4fa8' }} />
          </div>

          <h2>Forgot Password?</h2>
          <p className="login-subtitle">Enter your email to receive a reset link</p>

          {error && (
            <div className="login-error">
              <i className="ti ti-alert-circle" /> {error}
            </div>
          )}
          {success && (
            <div className="login-success">
              <i className="ti ti-circle-check" /> {success}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="login-field">
              <label htmlFor="forgot-email">Email Address</label>
              <div className="login-input-wrap">
                <i className="ti ti-mail" />
                <input
                  id="forgot-email"
                  type="email"
                  name="email"
                  placeholder="Enter your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  disabled={loading || !!success}
                />
              </div>
            </div>

            <CaptchaField
              key={captchaKey}
              value={captchaAnswer}
              onChange={setCaptchaAnswer}
              onCodeChange={setCaptchaExpected}
              disabled={loading || !!success}
              inputId="forgot-captcha"
            />

            <button
              type="submit"
              className="login-btn"
              disabled={loading || !!success}
            >
              {loading ? (
                <>
                  <i className="ti ti-loader" /> Sending…
                </>
              ) : (
                <>
                  <i className="ti ti-send" /> Send Reset Link
                </>
              )}
            </button>
          </form>

          <p className="set-password-back">
            <Link to="/login">Back to Sign In</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
